import React from 'react';
import { StarIcon, QuoteIcon } from 'lucide-react';
export function Testimonials() {
  return <section id="testimonials" className="bg-[#0F0F1A] py-24 px-6" data-id="element-206">
      <div className="max-w-6xl mx-auto" data-id="element-207">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-208">
          <p className="text-[#F72585] text-sm font-bold tracking-[0.3em] uppercase mb-4" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-209">
            Social Proof
          </p>
          <h2 className="text-[56px] sm:text-[72px] lg:text-[88px] text-white leading-none" style={{
          fontFamily: "'Bebas Neue', sans-serif"
        }} data-id="element-210">
            REAL RESULTS FROM{' '}
            <span className="text-[#F72585] glow-text-pink" data-id="element-211">
              REAL ADHD BRAINS
            </span>
          </h2>
        </div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12" data-id="element-212">
          <TestimonialCard quote="I've tried every productivity system out there. GTD, Pomodoro, time blocking — none of it worked for my brain. ADHD Reset was the first thing that actually clicked. I finished my first real project in 3 years." name="Marcus T." age={34} diagnosis="ADHD-Combined, diagnosed at 28" result="Finished first project in 3 years" resultColor="#4361EE" data-id="element-213" />
          <TestimonialCard quote="The hyperfocus hacking module alone was worth 10x the price. I went from spending 4 hours on Reddit to spending 4 hours on my actual work. My boss noticed within a week." name="Priya K." age={29} diagnosis="ADHD-Inattentive, diagnosed at 26" result="Promoted within 2 months" resultColor="#F72585" featured data-id="element-214" />
          <TestimonialCard quote="I was skeptical because I've been 'managing' my ADHD for 15 years. But the Task Gravity System changed everything. I now end every day knowing I did the right things, not just the easy things." name="Jordan W." age={41} diagnosis="ADHD-Combined, diagnosed at 12" result="Launched side business in 6 weeks" resultColor="#4361EE" data-id="element-215" />
        </div>

        {/* Trust bar */}
        <div className="flex flex-wrap justify-center gap-8 items-center" data-id="element-216">
          <TrustStat number="10,247" label="Copies sold" data-id="element-217" />
          <div className="w-px h-10 bg-white/10 hidden sm:block" data-id="element-218" />
          <TrustStat number="4.9/5" label="Average rating" data-id="element-219" />
          <div className="w-px h-10 bg-white/10 hidden sm:block" data-id="element-220" />
          <TrustStat number="94%" label="Report measurable improvement" data-id="element-221" />
          <div className="w-px h-10 bg-white/10 hidden sm:block" data-id="element-222" />
          <TrustStat number="30-day" label="Money-back guarantee" data-id="element-223" />
        </div>
      </div>
    </section>;
}
type TestimonialCardProps = {
  quote: string;
  name: string;
  age: number;
  diagnosis: string;
  result: string;
  resultColor: string;
  featured?: boolean;
};
function TestimonialCard({
  quote,
  name,
  age,
  diagnosis,
  result,
  resultColor,
  featured
}: TestimonialCardProps) {
  return <div className="relative rounded-2xl p-7 border transition-all duration-300 flex flex-col" style={{
    background: featured ? 'linear-gradient(135deg, #0F3460 0%, #16213E 100%)' : '#16213E',
    borderColor: featured ? '#4361EE' : 'rgba(255,255,255,0.05)',
    boxShadow: featured ? '0 0 30px rgba(67, 97, 238, 0.3), 0 0 60px rgba(67, 97, 238, 0.1)' : 'none'
  }} data-id="element-224">
      {featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4361EE] text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-widest" style={{
      fontFamily: "'Inter', sans-serif"
    }} data-id="element-225">
          Most Shared
        </div>}

      {/* Stars */}
      <div className="flex gap-1 mb-4" data-id="element-226">
        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 text-[#FFD60A] fill-[#FFD60A]" data-id="element-227" />)}
      </div>

      {/* Quote icon */}
      <QuoteIcon className="w-8 h-8 text-[#4361EE]/40 mb-3" data-id="element-228" />

      {/* Quote text */}
      <blockquote className="text-white/90 text-base leading-relaxed italic mb-6 flex-1" style={{
      fontFamily: "'Inter', sans-serif"
    }} data-id="element-229">
        "{quote}"
      </blockquote>

      {/* Result highlight */}
      <div className="rounded-lg px-4 py-2 mb-5 inline-block" style={{
      background: resultColor + '15',
      borderLeft: `3px solid ${resultColor}`
    }} data-id="element-230">
        <p className="text-sm font-bold" style={{
        fontFamily: "'Inter', sans-serif",
        color: resultColor
      }} data-id="element-231">
          ✓ {result}
        </p>
      </div>

      {/* Author */}
      <div className="flex items-center gap-3" data-id="element-232">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{
        background: resultColor + '30',
        border: `1px solid ${resultColor}40`,
        fontFamily: "'Inter', sans-serif"
      }} data-id="element-233">
          {name.charAt(0)}
        </div>
        <div data-id="element-234">
          <p className="text-white font-bold text-sm" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-235">
            {name}, {age}
          </p>
          <p className="text-[#A0AEC0] text-xs" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-236">
            {diagnosis}
          </p>
        </div>
      </div>
    </div>;
}
function TrustStat({
  number,
  label
}: {
  number: string;
  label: string;
}) {
  return <div className="text-center" data-id="element-237">
      <p className="text-white text-3xl sm:text-4xl leading-none mb-1 glow-text-blue" style={{
      fontFamily: "'Bebas Neue', sans-serif",
      color: '#4361EE'
    }} data-id="element-238">
        {number}
      </p>
      <p className="text-[#A0AEC0] text-sm" style={{
      fontFamily: "'Inter', sans-serif"
    }} data-id="element-239">
        {label}
      </p>
    </div>;
}